import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { useI18n } from "../../i18n/LanguageContext";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import { toast } from "sonner";
import { Pencil, Plus, Trash2, X } from "lucide-react";

const EMPTY = {
  order: 1, type: "free", estimated_minutes: 8,
  title: { en: "", ar: "" }, summary: { en: "", ar: "" },
  content_blocks: [],
};

export function AdminModules({ course }) {
  const { t, lang } = useI18n();
  const [rows, setRows] = useState(null);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setRows(null);
    try { setRows(await api.adminListModules(course.course_id)); }
    catch (e) { toast.error(e.message); setRows([]); }
  };
  useEffect(() => { load(); }, [course.course_id]);

  const openCreate = () => {
    const nextOrder = (rows && rows.length ? Math.max(...rows.map((r) => r.order)) + 1 : 1);
    setEditing({ ...EMPTY, order: nextOrder, course_id: course.course_id });
  };
  const openEdit = (m) => setEditing({ ...m });

  const save = async () => {
    setSaving(true);
    try {
      if (editing.module_id) {
        const { module_id, course_id, created_at, updated_at, ...body } = editing;
        await api.adminPatchModule(module_id, body);
      } else {
        await api.adminCreateModule(editing);
      }
      toast.success(t("admin.updated"));
      setEditing(null);
      await load();
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  const del = async (m) => {
    if (!window.confirm(t("admin.confirmDelete"))) return;
    try { await api.adminDeleteModule(m.module_id); toast.success("✓"); await load(); }
    catch (e) { toast.error(e.message); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="text-sm text-black/60">{course.course_id}</div>
        <Button onClick={openCreate} className="rounded-full bg-[#4361EE] text-white font-bold px-4"
                data-testid="admin-new-module-btn">
          <Plus className="h-4 w-4 me-1" /> {t("admin.newModule")}
        </Button>
      </div>

      {rows === null ? (
        <div className="text-black/50">{t("superadmin.loading")}</div>
      ) : rows.length === 0 ? (
        <div className="rounded-2xl border-2 border-dashed border-black/10 p-8 text-center text-black/50">{t("superadmin.empty")}</div>
      ) : (
        <ul className="space-y-2">
          {rows.map((m) => (
            <li key={m.module_id}
                className="rounded-2xl bg-white border border-black/5 p-4 flex items-center gap-3"
                data-testid={`admin-module-${m.module_id}`}>
              <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-[#4361EE]/10 text-[#4361EE] font-black text-sm">
                {m.order}
              </span>
              <div className="flex-1 min-w-0">
                <div className="font-bold truncate">{m.title?.[lang] || m.title?.en}</div>
                <div className="text-xs text-black/50 truncate">{m.summary?.[lang] || m.summary?.en}</div>
              </div>
              <Badge className="text-[10px]" variant={m.type === "paid" ? "default" : m.type === "exam" ? "destructive" : "secondary"}>
                {m.type}
              </Badge>
              <Button variant="ghost" onClick={() => openEdit(m)} className="rounded-full"
                      data-testid={`admin-edit-module-${m.module_id}`}>
                <Pencil className="h-4 w-4" />
              </Button>
              <Button variant="ghost" onClick={() => del(m)} className="rounded-full text-[#D90429]"
                      data-testid={`admin-delete-module-${m.module_id}`}>
                <Trash2 className="h-4 w-4" />
              </Button>
            </li>
          ))}
        </ul>
      )}

      {editing && (
        <div className="fixed inset-0 z-[60] bg-black/40 flex items-start justify-center overflow-y-auto p-4"
             data-testid="admin-module-editor">
          <div className="w-full max-w-xl bg-white rounded-3xl shadow-2xl p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-black text-lg">{editing.module_id ? t("admin.updated") : t("admin.newModule")}</h3>
              <button onClick={() => setEditing(null)} className="rounded-full p-2 hover:bg-black/5"><X className="h-5 w-5" /></button>
            </div>
            <div className="grid md:grid-cols-2 gap-3">
              <div><Label className="text-xs font-bold">{t("admin.order")}</Label>
                <Input type="number" value={editing.order}
                       onChange={(e) => setEditing({ ...editing, order: parseInt(e.target.value || "1", 10) })}
                       data-testid="admin-module-order" /></div>
              <div><Label className="text-xs font-bold">{t("admin.type")}</Label>
                <select value={editing.type} onChange={(e) => setEditing({ ...editing, type: e.target.value })}
                        className="w-full rounded-md border border-black/10 h-10 px-3 font-semibold bg-white"
                        data-testid="admin-module-type">
                  <option value="free">free</option><option value="paid">paid</option><option value="exam">exam</option>
                </select></div>
            </div>
            <div>
              <Label className="text-xs font-bold">{t("admin.titleEn")}</Label>
              <Input value={editing.title.en} onChange={(e) => setEditing({ ...editing, title: { ...editing.title, en: e.target.value } })}
                     data-testid="admin-module-title-en" />
            </div>
            <div>
              <Label className="text-xs font-bold">{t("admin.titleAr")}</Label>
              <Input dir="rtl" value={editing.title.ar} onChange={(e) => setEditing({ ...editing, title: { ...editing.title, ar: e.target.value } })}
                     data-testid="admin-module-title-ar" />
            </div>
            <div>
              <Label className="text-xs font-bold">{t("admin.summaryEn")}</Label>
              <Textarea rows={2} value={editing.summary.en} onChange={(e) => setEditing({ ...editing, summary: { ...editing.summary, en: e.target.value } })}
                        data-testid="admin-module-summary-en" />
            </div>
            <div>
              <Label className="text-xs font-bold">{t("admin.summaryAr")}</Label>
              <Textarea dir="rtl" rows={2} value={editing.summary.ar} onChange={(e) => setEditing({ ...editing, summary: { ...editing.summary, ar: e.target.value } })}
                        data-testid="admin-module-summary-ar" />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setEditing(null)} className="rounded-full">
                {t("admin.cancel")}
              </Button>
              <Button onClick={save} disabled={saving || !editing.title.en}
                      className="rounded-full bg-[#4361EE] text-white font-bold px-6 shadow-[0_3px_0_#2A3EB1]"
                      data-testid="admin-module-save">
                {saving ? "…" : t("admin.save")}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
